// Big on-screen target + instruction to chat (`!ready`) + live counter of submissions
// per platform. Drives the backend's broadcast-delay calibration routine (GDD §9.5).

import {
  CircleGeometry,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  type Texture,
} from 'three';
import { textTexture } from './lib/text-texture.js';
import { FONT_DISPLAY, FONT_UI, STAGE_HEIGHT, STAGE_WIDTH } from './lib/theme.js';

export interface CalibrationHandle {
  group: Object3D;
  show: () => void;
  hide: () => void;
  setCounts: (twitch: number, youtube: number, kick: number) => void;
  setResult: (measuredDelayMs: number | null, samples: number) => void;
  dispose: () => void;
}

export function createCalibration(): CalibrationHandle {
  const group = new Object3D();
  group.name = 'calibration';
  group.visible = false;
  group.userData.dataScene = 'calibration';

  const scrim = new Mesh(
    new PlaneGeometry(STAGE_WIDTH, STAGE_HEIGHT),
    new MeshBasicMaterial({ color: 0x02051a, transparent: true, opacity: 0.85 }),
  );
  scrim.renderOrder = 300;
  group.add(scrim);

  const target = new Mesh(
    new CircleGeometry(240, 48),
    new MeshBasicMaterial({ color: 0xffc43d }),
  );
  target.position.set(0, 100, 0);
  target.renderOrder = 310;
  group.add(target);
  const innerDisc = new Mesh(
    new CircleGeometry(140, 48),
    new MeshBasicMaterial({ color: 0xe23e3e }),
  );
  innerDisc.position.set(0, 100, 0.1);
  innerDisc.renderOrder = 311;
  group.add(innerDisc);

  const instructionMat = new MeshBasicMaterial({ transparent: true });
  const instruction = new Mesh(new PlaneGeometry(1600, 120), instructionMat);
  instruction.position.set(0, -180, 0);
  instruction.renderOrder = 320;
  group.add(instruction);
  const iTex: Texture = textTexture('Type !ready when you see this', {
    font: `72px ${FONT_DISPLAY}`,
    color: '#FFFFFF',
    width: 1600,
    height: 120,
    weight: 'bold',
    stroke: '#000000',
    strokeWidth: 4,
  });
  instructionMat.map = iTex;
  instructionMat.needsUpdate = true;

  const countsMat = new MeshBasicMaterial({ transparent: true });
  const counts = new Mesh(new PlaneGeometry(1200, 80), countsMat);
  counts.position.set(0, -300, 0);
  counts.renderOrder = 320;
  group.add(counts);

  const resultMat = new MeshBasicMaterial({ transparent: true });
  const result = new Mesh(new PlaneGeometry(1200, 80), resultMat);
  result.position.set(0, -400, 0);
  result.renderOrder = 320;
  group.add(result);

  function setCounts(twitch: number, youtube: number, kick: number): void {
    const tex: Texture = textTexture(`T: ${twitch}   Y: ${youtube}   K: ${kick}`, {
      font: `48px ${FONT_UI}`,
      color: '#FFFFFF',
      width: 1200,
      height: 80,
      weight: 'bold',
    });
    if (countsMat.map && countsMat.map !== tex) countsMat.map.dispose();
    countsMat.map = tex;
    countsMat.needsUpdate = true;
  }

  function setResult(measured: number | null, samples: number): void {
    const label =
      measured === null
        ? `Calibrating… (${samples} samples)`
        : `Delay: ${(measured / 1000).toFixed(1)}s (${samples} samples)`;
    const tex: Texture = textTexture(label, {
      font: `44px ${FONT_UI}`,
      color: '#FFC43D',
      width: 1200,
      height: 80,
      weight: 'bold',
    });
    if (resultMat.map && resultMat.map !== tex) resultMat.map.dispose();
    resultMat.map = tex;
    resultMat.needsUpdate = true;
  }

  return {
    group,
    show() {
      group.visible = true;
    },
    hide() {
      group.visible = false;
    },
    setCounts,
    setResult,
    dispose() {
      scrim.geometry.dispose();
      (scrim.material as MeshBasicMaterial).dispose();
      target.geometry.dispose();
      (target.material as MeshBasicMaterial).dispose();
      innerDisc.geometry.dispose();
      (innerDisc.material as MeshBasicMaterial).dispose();
      instructionMat.dispose();
      instruction.geometry.dispose();
      countsMat.dispose();
      counts.geometry.dispose();
      resultMat.dispose();
      result.geometry.dispose();
    },
  };
}
